import { useEffect, useMemo, useState } from "react";
import { useLeague } from "./LeagueLayout";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Stethoscope, ArrowLeft, Sparkles, CheckCircle2, AlertTriangle, TrendingUp, TrendingDown, Search, Archive, Clock } from "lucide-react";
import PlayerTimelineDialog from "../../components/PlayerTimelineDialog";

const ROLE_STYLES = {
  P: "bg-amber-400/30 text-amber-200 border-amber-400/60",
  D: "bg-emerald-400/30 text-emerald-200 border-emerald-400/60",
  C: "bg-blue-400/30 text-blue-200 border-blue-400/60",
  A: "bg-rose-400/30 text-rose-200 border-rose-400/60",
};

const LISTING_STYLES = {
  LISTED_ACTIVE: { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/40", label: "✅ ATTIVO" },
  TEMPORARILY_UNLISTED: { bg: "bg-slate-600/25", text: "text-slate-300", border: "border-slate-500/40", label: "🚫 TEMP UNLISTED" },
  LISTED_CEDUTO: { bg: "bg-orange-500/15", text: "text-orange-300", border: "border-orange-500/40", label: "🏳️ CEDUTO" },
  RELISTED: { bg: "bg-cyan-500/15", text: "text-cyan-300", border: "border-cyan-500/40", label: "✨ RELISTED" },
};

const REASON_STYLES = {
  no_fanta_id: { bg: "bg-rose-500/15", text: "text-rose-300", border: "border-rose-500/40", icon: "🚫", label: "SENZA FANTA_ID" },
  not_in_rose_file: { bg: "bg-amber-500/15", text: "text-amber-300", border: "border-amber-500/40", icon: "🔎", label: "NON NEL FILE ROSE" },
  shadow_linked: { bg: "bg-purple-500/15", text: "text-purple-300", border: "border-purple-500/40", icon: "🟣", label: "SHADOW ATTIVO" },
  other: { bg: "bg-slate-500/15", text: "text-slate-300", border: "border-slate-500/40", icon: "❓", label: "DA VERIFICARE" },
};

function fmt(n) { return Math.round(Number(n || 0)).toLocaleString("it-IT"); }

export default function LeagueSaluteRosa() {
  const { league, isAdmin } = useLeague();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [unresolved, setUnresolved] = useState([]);
  const [selections, setSelections] = useState({});
  const [applying, setApplying] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [filter, setFilter] = useState("all"); // all | no_fanta_id | not_in_rose_file | with_candidates | without_candidates
  const [timeline, setTimeline] = useState(null); // { fanta_id, name }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leagues/${league.league_id}/players/unresolved-suggestions`);
      const items = r.data.unresolved || [];
      setUnresolved(items);
      const preselect = {};
      items.forEach(u => {
        const top = u.candidates?.[0];
        if (top && top.score >= 0.85) preselect[u.player_id] = top.fanta_id;
      });
      setSelections(preselect);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) load(); }, [league.league_id, isAdmin]);

  const filtered = useMemo(() => {
    return unresolved.filter(u => {
      if (filter === "all") return true;
      if (filter === "no_fanta_id") return u.reason === "no_fanta_id";
      if (filter === "shadow_linked") return u.reason === "shadow_linked";
      if (filter === "not_in_rose_file") return u.reason === "not_in_rose_file";
      if (filter === "with_candidates") return (u.candidates?.length || 0) > 0;
      if (filter === "without_candidates") return (u.candidates?.length || 0) === 0;
      return true;
    });
  }, [unresolved, filter]);

  const stats = useMemo(() => {
    const total = unresolved.length;
    const noFid = unresolved.filter(u => u.reason === "no_fanta_id").length;
    const notInFile = unresolved.filter(u => u.reason === "not_in_rose_file").length;
    const shadow = unresolved.filter(u => u.reason === "shadow_linked").length;
    const withCand = unresolved.filter(u => (u.candidates?.length || 0) > 0).length;
    const highConf = unresolved.filter(u => (u.candidates?.[0]?.score || 0) >= 0.85).length;
    return { total, noFid, notInFile, shadow, withCand, highConf };
  }, [unresolved]);

  const bulkAttach = async () => {
    if (!window.confirm("🪄 Attaccare AUTOMATICAMENTE i fanta_id?\n\n• Match forti (≥85%) → fanta_id ufficiale dal listone\n• Match deboli → fanta_id_shadow persistente (auto-recupero al rientro nel listone)\n\nSnapshot pre-operazione creato automaticamente.")) return;
    setApplying(true);
    try {
      const r = await api.post(`/leagues/${league.league_id}/players/bulk-attach-fanta-id`);
      toast.success(`✅ ${r.data.real_matches} ufficiali + ${r.data.shadow_matches} shadow (su ${r.data.orphans_total} totali)`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  const reconcile = async () => {
    if (!window.confirm("♻️ Riconcilia lo stato dei giocatori con il listone corrente?\n\n• I giocatori marcati TEMP UNLISTED ma presenti oggi nel listone → tornano LISTED_ACTIVE\n• Q.I. e Q.A. aggiornate dal listone corrente\n• In fase < 8: salary azzerato\n\nSnapshot pre-operazione creato.")) return;
    setApplying(true);
    try {
      const r = await api.post(`/leagues/${league.league_id}/players/reconcile-listing-status`);
      toast.success(`♻️ ${r.data.updated_to_active} riattivati · ${r.data.updated_qa_only} Q.A. refresh · ${r.data.salary_zeroed} salary → 0`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  const applyResolutions = async () => {
    const resolutions = Object.entries(selections)
      .filter(([, fid]) => !!fid)
      .map(([player_id, fanta_id]) => ({ player_id, fanta_id }));
    if (resolutions.length === 0) { toast.error("Nessuna selezione"); return; }
    if (!window.confirm(`Applicare ${resolutions.length} collegamenti fanta_id?`)) return;
    setApplying(true);
    try {
      const r = await api.post(`/leagues/${league.league_id}/players/bulk-resolve-fanta-id`, { resolutions });
      toast.success(`✅ ${r.data.applied} collegamenti applicati`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  const aiResolve = async () => {
    if (!window.confirm("🤖 Chiedere a Claude Sonnet 5 di collegare automaticamente tutti i giocatori? (Consuma credito AI)")) return;
    setAiBusy(true);
    try {
      const r = await api.post(`/leagues/${league.league_id}/players/ai-resolve-unresolved`);
      toast.success(`🤖 ${r.data.applied} collegamenti AI applicati (su ${r.data.considered} considerati)`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(false); }
  };

  if (!isAdmin) {
    return <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 text-center text-slate-400" data-testid="not-admin">Sezione riservata agli admin.</div>;
  }

  return (
    <div className="space-y-6" data-testid="salute-rosa-page">
      {/* Header */}
      <div className="rounded-3xl border border-amber-500/40 bg-gradient-to-br from-slate-900 via-slate-950 to-amber-950/30 p-6 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl pointer-events-none"/>
        <div className="relative flex items-start gap-4 flex-wrap">
          <button onClick={() => nav(`/lega/${league.league_id}/decisioni`)}
            data-testid="back-decisioni"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white uppercase tracking-widest shrink-0 mt-1">
            <ArrowLeft size={13}/> Decisioni
          </button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
              <Stethoscope className="text-amber-400" size={22}/>
            </div>
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white">🩺 Salute Rosa</h1>
              <p className="text-sm text-slate-300 mt-1">
                Risolvi i giocatori marcati &quot;Da verificare&quot;: senza collegamento al listone globale (fanta_id) o mancanti dall&apos;ultimo file rose CSV.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI + AI CTA */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi label="Da risolvere" v={stats.total} color="amber" testid="kpi-total"/>
        <Kpi label="Senza fanta_id" v={stats.noFid} color="rose" testid="kpi-nofid"/>
        <Kpi label="Shadow attivi" v={stats.shadow} color="purple" testid="kpi-shadow"/>
        <Kpi label="Non nel file rose" v={stats.notInFile} color="amber" testid="kpi-notinfile"/>
        <Kpi label="Con candidati" v={stats.withCand} color="emerald" testid="kpi-withcand"/>
        <Kpi label="Match forti ≥85%" v={stats.highConf} color="cyan" testid="kpi-highconf"/>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="salute-filters">
          {[
            { k: "all", l: "Tutti" },
            { k: "no_fanta_id", l: "🚫 Senza fanta_id" },
            { k: "shadow_linked", l: "🟣 Shadow" },
            { k: "not_in_rose_file", l: "🔎 Non nel file" },
            { k: "with_candidates", l: "✅ Con candidati" },
            { k: "without_candidates", l: "❌ Senza candidati" },
          ].map(t => (
            <button key={t.k} onClick={() => setFilter(t.k)} data-testid={`filter-${t.k}`}
              className={`px-2.5 py-1.5 rounded-md text-[11px] font-bold border transition-colors ${filter === t.k ? "bg-amber-500/20 border-amber-500/60 text-amber-200" : "bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-600"}`}>
              {t.l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Quick preselect */}
          <div className="flex items-center gap-1 border-l border-slate-800 pl-2 mr-1">
            <span className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Preseleziona:</span>
            <button onClick={() => {
              const s = {};
              unresolved.forEach(u => { const top = u.candidates?.[0]; if (top && top.score >= 0.85) s[u.player_id] = top.fanta_id; });
              setSelections(s);
              toast.success(`Preselezionati ${Object.keys(s).length} match ≥85%`);
            }} data-testid="preselect-85"
              className="px-2 py-1 rounded text-[10px] font-bold bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25">
              ≥85%
            </button>
            <button onClick={() => {
              const s = {};
              unresolved.forEach(u => { const top = u.candidates?.[0]; if (top && top.score >= 0.75) s[u.player_id] = top.fanta_id; });
              setSelections(s);
              toast.success(`Preselezionati ${Object.keys(s).length} match ≥75%`);
            }} data-testid="preselect-75"
              className="px-2 py-1 rounded text-[10px] font-bold bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25">
              ≥75%
            </button>
            <button onClick={() => setSelections({})} data-testid="preselect-clear"
              className="px-2 py-1 rounded text-[10px] font-bold bg-slate-800 border border-slate-700 text-slate-400 hover:bg-slate-700">
              Nessuno
            </button>
          </div>
          <button onClick={bulkAttach} disabled={applying || loading || stats.total === 0}
            data-testid="salute-bulk-attach"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-cyan-500/20 border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/30 disabled:opacity-40">
            🪄 Auto-Attach Fanta ID
          </button>
          <button onClick={reconcile} disabled={applying || loading}
            data-testid="salute-reconcile"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-40">
            ♻️ Riconcilia Listone
          </button>
          <button onClick={aiResolve} disabled={aiBusy || applying || loading || stats.total === 0}
            data-testid="salute-ai-resolve"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-purple-500/20 border border-purple-500/40 text-purple-200 hover:bg-purple-500/30 disabled:opacity-40">
            <Sparkles size={13}/> {aiBusy ? "AI…" : "🤖 Risolvi con AI"}
          </button>
          <button onClick={applyResolutions}
            disabled={applying || Object.values(selections).filter(Boolean).length === 0}
            data-testid="salute-apply-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-gradient-to-br from-emerald-400 to-emerald-600 text-slate-950 hover:brightness-110 disabled:opacity-40 shadow-lg shadow-emerald-500/20">
            <CheckCircle2 size={13}/> {applying ? "Applico…" : `Applica ${Object.values(selections).filter(Boolean).length}`}
          </button>
        </div>
      </div>

      {/* Note: TEMPORARILY_UNLISTED candidates + Shadow */}
      <div className="rounded-xl bg-slate-900/50 border border-slate-800 p-3 flex items-start gap-2 text-xs text-slate-400" data-testid="salute-tip">
        <span className="text-amber-400 shrink-0">💡</span>
        <div className="space-y-1">
          <div>
            I candidati marcati <strong className="text-slate-300">🚫 TEMP UNLISTED</strong> sono giocatori con <em>fanta_id storico</em> ma temporaneamente fuori dal listone corrente (es. Pellegrini Roma dopo un import).
            Collegarli è <strong className="text-emerald-300">sicuro</strong>: la proprietà fantasy resta invariata e il valore viene <em>congelato</em>.
          </div>
          <div>
            🟣 <strong className="text-purple-300">Shadow attivi</strong>: giocatori senza fanta_id ufficiale (es. Vlahovic dopo un trasferimento all&apos;estero) ma con un <em>fanta_id_shadow</em> persistente. Verranno <strong className="text-emerald-300">auto-collegati al fanta_id ufficiale</strong> non appena rientreranno nel listone.
          </div>
          <div>
            🪄 <strong className="text-cyan-300">Auto-Attach</strong>: un click risolve tutti — match forti al fanta_id ufficiale, resto in shadow.
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-10 text-center text-slate-400" data-testid="salute-loading">Carico suggerimenti…</div>
      ) : stats.total === 0 ? (
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-10 text-center" data-testid="salute-empty">
          <div className="text-3xl mb-2">🎉</div>
          <div className="text-emerald-300 font-bold text-lg">Tutti i giocatori sono collegati al listone!</div>
          <div className="text-xs text-slate-400 mt-1">La rosa è in salute perfetta.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(u => (
            <PlayerCard key={u.player_id} item={u}
              selectedFid={selections[u.player_id]}
              onSelect={(fid) => setSelections(prev => ({ ...prev, [u.player_id]: prev[u.player_id] === fid ? null : fid }))}
              onOpenTimeline={(fid, name) => setTimeline({ fanta_id: fid, name })}/>
          ))}
        </div>
      )}

      <PlayerTimelineDialog
        open={!!timeline}
        fantaId={timeline?.fanta_id}
        playerName={timeline?.name}
        onClose={() => setTimeline(null)}
      />
    </div>
  );
}

function Kpi({ label, v, color, testid }) {
  const colorClass = {
    amber: "text-amber-300 border-amber-500/40",
    rose: "text-rose-300 border-rose-500/40",
    emerald: "text-emerald-300 border-emerald-500/40",
    cyan: "text-cyan-300 border-cyan-500/40",
    purple: "text-purple-300 border-purple-500/40",
  }[color] || "text-white border-slate-800";
  return (
    <div className={`rounded-2xl border bg-gradient-to-br from-slate-900 to-slate-950 p-4 shadow-lg ${colorClass.split(" ")[1]}`} data-testid={testid}>
      <div className="text-[10px] tracking-widest text-slate-500 uppercase font-bold">{label}</div>
      <div className={`text-3xl font-mono font-black mt-1 ${colorClass.split(" ")[0]}`}>{v}</div>
    </div>
  );
}

function PlayerCard({ item, selectedFid, onSelect, onOpenTimeline }) {
  const reasonStyle = REASON_STYLES[item.reason] || REASON_STYLES.other;
  const listing = LISTING_STYLES[item.listing_status] || LISTING_STYLES.LISTED_ACTIVE;
  const roleStyle = ROLE_STYLES[item.role] || "bg-slate-700 text-slate-300";
  const roiPos = (item.roi_pct || 0) >= 0;

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4" data-testid={`salute-item-${item.player_id}`}>
      {/* Header giocatore con TUTTI i dettagli */}
      <div className="flex items-start gap-3 flex-wrap mb-3 pb-3 border-b border-slate-800">
        <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-black text-sm border ${roleStyle} shrink-0`}>{item.role || "?"}</span>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-black text-white text-base">{item.name}</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-bold tracking-widest ${reasonStyle.bg} ${reasonStyle.text} ${reasonStyle.border}`} data-testid={`salute-reason-${item.player_id}`}>
              {reasonStyle.icon} {reasonStyle.label}
            </span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-bold tracking-widest ${listing.bg} ${listing.text} ${listing.border}`}>
              {listing.label}
            </span>
            {item.fanta_id_shadow && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full border font-bold tracking-widest bg-purple-500/15 text-purple-300 border-purple-500/40 font-mono" title="Shadow ID: si riaggancia automaticamente al fanta_id ufficiale se il giocatore rientra nel listone">
                🟣 {item.fanta_id_shadow}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
            <span>🏟️ <strong className="text-slate-200">{item.real_team || "—"}</strong></span>
            <span className="text-slate-600">·</span>
            <span>👥 {item.team_name || "?"}</span>
            {item.roles_mantra?.length > 0 && (
              <>
                <span className="text-slate-600">·</span>
                <span className="flex items-center gap-1">
                  <span className="text-[10px] text-slate-500">Mantra:</span>
                  {item.roles_mantra.map((rm, i) => (
                    <span key={i} className="text-[10px] px-1 py-0.5 rounded bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-300 font-black">{rm}</span>
                  ))}
                </span>
              </>
            )}
          </div>
          <div className="text-[10px] text-slate-500 mt-1 italic leading-relaxed">
            💡 {item.reason_detail}
          </div>
        </div>
        {/* Stats economici */}
        <div className="flex items-center gap-3 text-right shrink-0">
          <MiniStat label="Pagato" v={`${fmt(item.prezzo_pagato)}cr`}/>
          <MiniStat label="Stip." v={`${fmt(item.salary)}cr`}/>
          <MiniStat label="Q.I." v={fmt(item.quotazione_iniziale)}/>
          <MiniStat label="Q.A." v={fmt(item.quotazione_attuale)} accent={item.quotazione_attuale >= item.quotazione_iniziale ? "text-emerald-300" : "text-rose-300"}/>
          <MiniStat label="Valore" v={`${fmt(item.valore_rivalutato)}cr`} accent="text-amber-300"/>
          <MiniStat label="ROI"
            v={<span className={`inline-flex items-center gap-0.5 ${roiPos ? "text-emerald-300" : "text-rose-300"}`}>
              {roiPos ? <TrendingUp size={11}/> : <TrendingDown size={11}/>}
              {roiPos ? "+" : ""}{Math.round(item.roi_pct || 0)}%
            </span>}
          />
        </div>
      </div>

      {/* Candidati */}
      {!item.candidates?.length && !item.historical_candidates?.length ? (
        <HistorySearchInline item={item} selectedFid={selectedFid} onSelect={onSelect} onOpenTimeline={onOpenTimeline}/>
      ) : (
        <div className="space-y-3">
          {item.candidates?.length > 0 && (            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Candidati listone corrente</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {item.candidates.map(c => {
                  const selected = selectedFid === c.fanta_id;
                  const candListing = LISTING_STYLES[c.listing_status] || LISTING_STYLES.LISTED_ACTIVE;
                  const scorePct = Math.round(c.score * 100);
                  const scoreColor = c.score >= 0.85 ? "text-emerald-300" : c.score >= 0.75 ? "text-amber-300" : "text-slate-400";
                  return (
                    <button key={c.fanta_id}
                      onClick={() => onSelect(c.fanta_id)}
                      data-testid={`salute-cand-${item.player_id}-${c.fanta_id}`}
                      className={`text-left p-2.5 rounded-xl border transition-all relative ${selected ? "border-amber-400 bg-amber-500/15 ring-2 ring-amber-400/40" : "border-slate-800 hover:border-slate-600 bg-slate-950/40"}`}>
                      {onOpenTimeline && (
                        <span onClick={(ev) => { ev.stopPropagation(); onOpenTimeline(c.fanta_id, c.name); }}
                          role="button" tabIndex={0}
                          data-testid={`salute-cand-timeline-${item.player_id}-${c.fanta_id}`}
                          title="Timeline multi-stagione"
                          className="absolute top-1.5 right-1.5 p-1 rounded bg-purple-500/15 border border-purple-500/40 text-purple-300 hover:bg-purple-500/25 cursor-pointer">
                          <Clock size={11}/>
                        </span>
                      )}
                      <div className="flex items-center gap-1.5 flex-wrap pr-6">
                        <span className="font-bold text-sm text-slate-100">{c.name}</span>
                        <span className={`text-[10px] font-mono font-black ${scoreColor}`}>{scorePct}%</span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{c.real_team}</div>
                      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-bold ${candListing.bg} ${candListing.text} ${candListing.border}`}>
                          {candListing.label}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">Q.A. {c.qa_current ?? c.last_valid_qa ?? "?"}</span>
                        {c.roles_mantra?.length > 0 && (
                          <span className="text-[9px] px-1 py-0.5 rounded bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-300 font-black">
                            {c.roles_mantra.join("/")}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {item.historical_candidates?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-purple-400 font-bold mb-2 flex items-center gap-1">
                <Archive size={11}/> Candidati archivio storico multi-stagione
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {item.historical_candidates.map(hc => {
                  const selected = selectedFid === hc.fanta_id;
                  const scorePct = Math.round(hc.score * 100);
                  const scoreColor = hc.score >= 0.85 ? "text-emerald-300" : hc.score >= 0.75 ? "text-amber-300" : "text-slate-400";
                  return (
                    <button key={`hist-${hc.fanta_id}`}
                      onClick={() => onSelect(hc.fanta_id)}
                      data-testid={`salute-hcand-${item.player_id}-${hc.fanta_id}`}
                      className={`text-left p-2.5 rounded-xl border transition-all relative ${selected ? "border-amber-400 bg-amber-500/15 ring-2 ring-amber-400/40" : "border-purple-500/30 hover:border-purple-500/60 bg-purple-500/5"}`}>
                      {onOpenTimeline && (
                        <span onClick={(ev) => { ev.stopPropagation(); onOpenTimeline(hc.fanta_id, hc.name); }}
                          role="button" tabIndex={0}
                          data-testid={`salute-hcand-timeline-${item.player_id}-${hc.fanta_id}`}
                          title="Timeline multi-stagione"
                          className="absolute top-1.5 right-1.5 p-1 rounded bg-purple-500/25 border border-purple-500/50 text-purple-200 hover:bg-purple-500/40 cursor-pointer">
                          <Clock size={11}/>
                        </span>
                      )}
                      <div className="flex items-center gap-1.5 flex-wrap pr-6">
                        <span className="text-[9px] font-mono font-black px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/40 shrink-0">{hc.season}</span>
                        <span className="font-bold text-sm text-slate-100">{hc.name}</span>
                        <span className={`text-[10px] font-mono font-black ${scoreColor}`}>{scorePct}%</span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{hc.real_team}</div>
                      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full border font-bold bg-purple-500/15 text-purple-300 border-purple-500/40">
                          📚 STORICO
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">Q.A. {Math.round(hc.qa || 0)}</span>
                        {hc.roles_mantra?.length > 0 && (
                          <span className="text-[9px] px-1 py-0.5 rounded bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-300 font-black">
                            {hc.roles_mantra.join("/")}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {/* Iter 69.3: assegna manualmente — sempre disponibile per gestire nomi molto storpiati */}
          <HistorySearchInline item={item} selectedFid={selectedFid} onSelect={onSelect} onOpenTimeline={onOpenTimeline}/>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, v, accent }) {
  return (
    <div className="text-right">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className={`text-xs font-mono font-black ${accent || "text-slate-200"}`}>{v}</div>
    </div>
  );
}

/**
 * Iter 69: ricerca in archivio storico per giocatori senza candidati nel listone corrente.
 * Permette all'admin di scegliere manualmente un fanta_id dal database multi-stagione.
 */
function HistorySearchInline({ item, selectedFid, onSelect, onOpenTimeline }) {
  const [query, setQuery] = useState(item.name || "");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false);

  const runSearch = async () => {
    if ((query || "").trim().length < 2) { toast.error("Almeno 2 caratteri"); return; }
    setBusy(true);
    try {
      const r = await api.get(`/rooster/search-all?q=${encodeURIComponent(query.trim())}&limit=25`);
      setResults(r.data.results || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-purple-500/30 bg-purple-500/5 p-3" data-testid={`salute-history-search-${item.player_id}`}>
      <div className="flex items-center gap-2 text-xs text-slate-300 mb-2">
        <Archive size={14} className="text-purple-400"/>
        <span>🔧 <strong>Assegna manualmente</strong>: cerca in listone corrente + archivio storico</span>
        {!opened && (
          <button onClick={() => { setOpened(true); runSearch(); }}
            data-testid={`salute-history-open-${item.player_id}`}
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded bg-purple-500/20 border border-purple-500/40 text-purple-200 font-bold text-[10px]">
            <Search size={11}/> Apri ricerca
          </button>
        )}
      </div>
      {opened && (
        <>
          <div className="flex items-center gap-2 mb-2">
            <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && runSearch()}
              placeholder="Nome giocatore anche parziale (es. 'vlah', 'maripan')…"
              data-testid={`salute-history-input-${item.player_id}`}
              className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-3 py-1.5 text-xs"/>
            <button onClick={runSearch} disabled={busy}
              data-testid={`salute-history-search-btn-${item.player_id}`}
              className="px-3 py-1.5 rounded-lg bg-purple-500/20 border border-purple-500/40 text-purple-200 text-[10px] font-bold hover:bg-purple-500/30 disabled:opacity-40">
              {busy ? "…" : "Cerca"}
            </button>
          </div>
          {results.length === 0 ? (
            <div className="text-[11px] italic text-slate-500">Nessun risultato. Prova nome parziale o cognome solo.</div>
          ) : (
            <div className="max-h-56 overflow-y-auto space-y-1">
              {results.map((r, i) => {
                const selected = selectedFid === r.fanta_id;
                const isCurrent = r.source === "current";
                return (
                  <button key={`${r.source}-${r.fanta_id}-${i}`}
                    onClick={() => onSelect(r.fanta_id)}
                    data-testid={`salute-history-pick-${item.player_id}-${r.fanta_id}`}
                    className={`w-full text-left flex items-center gap-2 p-2 rounded-lg border transition-colors ${selected ? "border-amber-400 bg-amber-500/15 ring-2 ring-amber-400/40" : isCurrent ? "border-emerald-500/40 bg-emerald-500/5 hover:border-emerald-500/60" : "border-purple-500/30 hover:border-purple-500/60 bg-slate-950/40"}`}>
                    <span className={`text-[10px] font-mono font-black px-1.5 py-0.5 rounded border shrink-0 ${isCurrent ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-rose-500/15 text-rose-300 border-rose-500/40"}`}>
                      {isCurrent ? "🌐 CORRENTE" : r.season}
                    </span>
                    <span className="font-mono text-slate-500 text-[10px] shrink-0">#{r.fanta_id}</span>
                    <span className="font-bold text-white text-xs flex-1 truncate">{r.name}</span>
                    <span className="text-[10px] text-slate-400">{r.real_team}</span>
                    <span className="text-[9px] px-1 py-0.5 rounded bg-slate-800 text-slate-400 font-bold">{r.role_classic}</span>
                    <span className="text-[9px] text-slate-500 font-mono">Q.A. {Math.round(r.qa || 0)}</span>
                    {onOpenTimeline && (
                      <span onClick={(ev) => { ev.stopPropagation(); onOpenTimeline(r.fanta_id, r.name); }}
                        role="button" tabIndex={0}
                        data-testid={`salute-history-timeline-${item.player_id}-${r.fanta_id}`}
                        title="Timeline multi-stagione"
                        className="p-1 rounded bg-purple-500/15 border border-purple-500/40 text-purple-300 hover:bg-purple-500/25 shrink-0 cursor-pointer">
                        <Clock size={10}/>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
